import React, { useState, useEffect } from 'react';
import { Check, Edit2, Trash2, Plus, X, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { isAxiosError } from 'axios';
import api from '../../services/api';

interface Plano {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  features: string[];
  ativo: boolean;
  ordem: number;
}

interface HistoricoItem {
  descricao: string;
  data: string;
  tipo: 'criacao' | 'edicao' | 'exclusao';
}

const EMPTY_PLANO: Omit<Plano, 'id'> = {
  nome: '',
  descricao: '',
  preco: 0,
  features: [''],
  ativo: true,
  ordem: 0,
};

function extractErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const apiMessage = (err.response?.data as any)?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) return apiMessage;
    if (typeof err.message === 'string' && err.message.trim()) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export default function PlanosPage() {
  const [planos, setPlanos] = useState<Plano[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState<Plano | null>(null);
  const [form, setForm] = useState<Omit<Plano, 'id'>>(EMPTY_PLANO);
  const [salvando, setSalvando] = useState(false);

  // Confirmação de exclusão
  const [confirmDelete, setConfirmDelete] = useState<Plano | null>(null);

  useEffect(() => {
    carregarPlanos();
  }, []);

  async function carregarPlanos() {
    setLoading(true);
    setErro('');
    try {
      const resp = await api.get<Plano[]>('/saas/planos');
      const data = Array.isArray(resp.data) ? resp.data : [];
      setPlanos(data);
    } catch (err) {
      setErro('Erro ao carregar planos: ' + extractErrorMessage(err, 'Falha ao carregar planos'));
    } finally {
      setLoading(false);
    }
  }

  function abrirNovo() {
    setEditando(null);
    setForm({ ...EMPTY_PLANO, features: [''] });
    setShowModal(true);
  }

  function abrirEdicao(plano: Plano) {
    setEditando(plano);
    setForm({
      nome: plano.nome,
      descricao: plano.descricao,
      preco: plano.preco,
      features: plano.features.length > 0 ? [...plano.features] : [''],
      ativo: plano.ativo,
      ordem: plano.ordem,
    });
    setShowModal(true);
  }

  function fecharModal() {
    setShowModal(false);
    setEditando(null);
  }

  // ── features (recursos do plano) ───────────────────────────
  function setFeature(idx: number, valor: string) {
    setForm(f => {
      const features = [...f.features];
      features[idx] = valor;
      return { ...f, features };
    });
  }

  function adicionarFeature() {
    setForm(f => ({ ...f, features: [...f.features, ''] }));
  }

  function removerFeature(idx: number) {
    setForm(f => ({ ...f, features: f.features.filter((_, i) => i !== idx) }));
  }

  // ── Salvar (criar ou editar) ────────────────────────────────
  async function salvar() {
    if (!form.nome.trim()) { setErro('Informe o nome do plano'); return; }
    setSalvando(true);
    setErro('');
    try {
      const payload = {
        ...form,
        preco: Number(form.preco),
        features: form.features.filter(f => f.trim() !== ''),
      };

      let planoSalvo: Plano;
      if (editando) {
        const resp = await api.put<Plano>(`/saas/planos/${editando.id}`, payload);
        planoSalvo = resp.data;
      } else {
        const resp = await api.post<Plano>('/saas/planos', payload);
        planoSalvo = resp.data;
      }

      const msg = editando
        ? `Plano "${planoSalvo.nome}" atualizado para R$ ${Number(planoSalvo.preco).toFixed(0)}/mês`
        : `Plano "${planoSalvo.nome}" criado`;

      setHistorico(h => [
        { descricao: msg, data: new Date().toLocaleString('pt-BR'), tipo: editando ? 'edicao' : 'criacao' },
        ...h,
      ]);
      setSucesso(msg);
      setTimeout(() => setSucesso(''), 3500);
      fecharModal();
      await carregarPlanos();
    } catch (err) {
      setErro(extractErrorMessage(err, 'Falha ao salvar'));
    } finally {
      setSalvando(false);
    }
  }

  // ── Excluir ─────────────────────────────────────────────────
  async function confirmarExclusao() {
    if (!confirmDelete) return;
    try {
      await api.delete(`/saas/planos/${confirmDelete.id}`);
      setHistorico(h => [
        { descricao: `Plano "${confirmDelete.nome}" excluído`, data: new Date().toLocaleString('pt-BR'), tipo: 'exclusao' },
        ...h,
      ]);
      setSucesso(`Plano "${confirmDelete.nome}" excluído`);
      setTimeout(() => setSucesso(''), 3000);
      setConfirmDelete(null);
      await carregarPlanos();
    } catch (err) {
      setErro(extractErrorMessage(err, 'Falha ao excluir'));
      setConfirmDelete(null);
    }
  }

  function fmtPreco(v: number) {
    if (Number(v) === 0) return 'Customizado';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(Number(v));
  }

  const corBorda: Record<number, string> = { 0: '#cd7f32', 1: '#9b59b6', 2: '#2c3e50' };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white shadow-lg">
        <h1 className="text-4xl font-bold mb-2">Planos e Preços</h1>
        <p className="text-purple-100">Gerencie os planos de assinatura e seus preços</p>
      </div>

      {/* Alertas */}
      {erro && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffebee', border: '1px solid #ef5350', color: '#c62828', borderRadius: 8, padding: '12px 16px', fontSize: 14 }}>
          <AlertCircle size={18} /> {erro}
          <button onClick={() => setErro('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#c62828' }}>×</button>
        </div>
      )}
      {sucesso && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#e8f5e9', border: '1px solid #66bb6a', color: '#2e7d32', borderRadius: 8, padding: '12px 16px', fontSize: 14 }}>
          <CheckCircle size={18} /> {sucesso}
        </div>
      )}

      {/* Botão Novo Plano */}
      <div className="flex justify-end">
        <button
          onClick={abrirNovo}
          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-semibold"
        >
          <Plus size={20} /> Novo Plano
        </button>
      </div>

      {/* Grid de Planos */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
          <p style={{ marginTop: 12 }}>Carregando planos...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {planos.map((plano, idx) => (
            <div
              key={plano.id}
              className={`rounded-2xl p-6 border-2 transition-all ${
                plano.ativo ? 'bg-white shadow-lg' : 'bg-gray-50 opacity-75'
              }`}
              style={{ borderColor: plano.ativo ? (corBorda[idx] || '#e5e7eb') : '#e5e7eb' }}
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{plano.nome}</h3>
                <p className="text-gray-600 text-sm mt-1">{plano.descricao}</p>
              </div>

              <div className="mb-6">
                <div className="text-4xl font-bold text-gray-900">
                  {Number(plano.preco) === 0 ? (
                    <span className="text-lg">Customizado</span>
                  ) : (
                    <>
                      {fmtPreco(plano.preco)}
                      <span className="text-lg font-normal text-gray-600">/mês</span>
                    </>
                  )}
                </div>
              </div>

              <div className="mb-6 space-y-3">
                {(Array.isArray(plano.features) ? plano.features : []).map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{f}</span>
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${plano.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                  {plano.ativo ? 'Ativo' : 'Inativo'}
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => abrirEdicao(plano)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Edit2 size={18} /> Editar
                </button>
                <button
                  onClick={() => setConfirmDelete(plano)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center"
                  title="Excluir plano"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Histórico */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Histórico de Mudanças</h2>
        {historico.length === 0 ? (
          <p className="text-gray-500 text-sm">Nenhuma alteração nesta sessão.</p>
        ) : (
          <div className="space-y-4">
            {historico.map((h, i) => (
              <div key={i} className={`border-l-4 pl-4 py-2 ${h.tipo === 'criacao' ? 'border-green-400' : h.tipo === 'exclusao' ? 'border-red-400' : 'border-blue-400'}`}>
                <p className="text-sm text-gray-700">{h.descricao}</p>
                <p className="text-xs text-gray-500 mt-1">{h.data}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MODAL CRIAR / EDITAR ───────────────────────────────────── */}
      {showModal && (
        <div
          onClick={fecharModal}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          >
            {/* Header do modal */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 20, color: '#333' }}>
                {editando ? `Editar Plano — ${editando.nome}` : 'Novo Plano'}
              </h3>
              <button onClick={fecharModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Nome */}
              <div>
                <label style={labelStyle}>Nome do plano *</label>
                <input style={inputStyle} value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: Bronze, Profissional, Enterprise" />
              </div>

              {/* Descrição */}
              <div>
                <label style={labelStyle}>Descrição</label>
                <input style={inputStyle} value={form.descricao}
                  onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                  placeholder="Ex: Para pequenas creches" />
              </div>

              {/* Preço */}
              <div>
                <label style={labelStyle}>Preço mensal (R$) — Digite 0 para "Customizado"</label>
                <input style={inputStyle} type="number" min="0" step="1" value={form.preco}
                  onChange={e => setForm(f => ({ ...f, preco: Number(e.target.value) }))} />
              </div>

              {/* Ordem */}
              <div>
                <label style={labelStyle}>Ordem de exibição</label>
                <input style={inputStyle} type="number" min="0" step="1" value={form.ordem}
                  onChange={e => setForm(f => ({ ...f, ordem: Number(e.target.value) }))} />
              </div>

              {/* Status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="ativo" checked={form.ativo}
                  onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }} />
                <label htmlFor="ativo" style={{ cursor: 'pointer', fontSize: 14, color: '#333' }}>Plano ativo</label>
              </div>

              {/* Recursos (features) */}
              <div>
                <label style={labelStyle}>Recursos incluídos</label>
                {form.features.map((feat, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input
                      style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                      value={feat}
                      onChange={e => setFeature(idx, e.target.value)}
                      placeholder={`Recurso ${idx + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removerFeature(idx)}
                      disabled={form.features.length === 1}
                      style={{ padding: '0 10px', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 6, cursor: 'pointer', color: '#c62828' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={adicionarFeature}
                  style={{ fontSize: 13, color: '#7c3aed', background: 'none', border: '1px dashed #7c3aed', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', width: '100%' }}>
                  + Adicionar recurso
                </button>
              </div>

              {/* Erro local */}
              {erro && (
                <div style={{ background: '#ffebee', color: '#c62828', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                  {erro}
                </div>
              )}
            </div>

            {/* Ações */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button type="button" onClick={fecharModal} disabled={salvando}
                style={{ padding: '10px 20px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
                Cancelar
              </button>
              <button type="button" onClick={salvar} disabled={salvando}
                style={{ padding: '10px 24px', background: salvando ? '#9e9e9e' : '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, cursor: salvando ? 'wait' : 'pointer', fontWeight: 600, fontSize: 14 }}>
                {salvando ? 'Salvando...' : editando ? 'Salvar Alterações' : 'Criar Plano'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CONFIRMAR EXCLUSÃO ──────────────────────────────── */}
      {confirmDelete && (
        <div
          onClick={() => setConfirmDelete(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}
          >
            <Trash2 size={40} color="#ef4444" style={{ marginBottom: 12 }} />
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>Excluir Plano</h3>
            <p style={{ color: '#555', fontSize: 14, marginBottom: 20 }}>
              Tem certeza que deseja excluir o plano <strong>"{confirmDelete.nome}"</strong>?<br />
              Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setConfirmDelete(null)}
                style={{ padding: '10px 20px', border: '1px solid #ddd', background: '#fff', borderRadius: 8, cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={confirmarExclusao}
                style={{ padding: '10px 20px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box', marginBottom: 0,
};
